import { AtSign, Check, Inbox, MapPin, Phone, type LucideIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import type { CoreShowcase } from "./core-card";

interface VerifyService {
  /** Short name for the left-rail module line. */
  label: string;
  /** The product running the check, shown on the carousel row. */
  name: string;
  icon: LucideIcon;
}

/** The four checks, in the order the module line lists them. */
const VERIFY_SERVICES: VerifyService[] = [
  { label: "Address", name: "G-NAF Verify", icon: MapPin },
  { label: "Phone", name: "Phone Verify", icon: Phone },
  { label: "Email", name: "Email Verify", icon: AtSign },
  { label: "MailPoint", name: "MailPoint Verify", icon: Inbox },
];

/** How long the centre row sits "Checking" before its result lands. Well inside
 * CoreCard's per-panel dwell, so the tick reads before the row moves on. */
const CHECK_MS = 900;

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const StatusPill = ({ state }: { state: "idle" | "check" | "ok" }) => (
  <span
    className={cn(
      "flex items-center gap-1 rounded-full border px-2 py-0.5 font-mono text-[0.5625rem] tracking-[0.08em] whitespace-nowrap uppercase transition-colors",
      state === "ok" && "border-success/30 bg-success/10 text-success",
      state === "check" && "border-primary/30 text-primary",
      state === "idle" && "text-muted-foreground",
    )}
  >
    {state === "check" && <Spinner className="size-3" />}
    {state === "ok" && <Check className="size-3" />}
    {state === "ok" ? "Verified" : state === "check" ? "Checking" : "Ready"}
  </span>
);

/**
 * Verify's showcase plate: a slow vertical carousel of the four checks. The row
 * in the centre is being verified — it spins, then snaps to a tick — then rises
 * one row and stays ticked for a count before it wraps off the top and back in
 * from the bottom as "Ready". Driven by CoreCard's shared rotation `active`, so
 * the plate and the module line agree on which check is on screen.
 */
const VerifyCarousel = ({ active }: { active: number }) => {
  const count = VERIFY_SERVICES.length;
  const reduced = prefersReducedMotion();
  // Whether the centre row has finished its check this dwell. Under reduced
  // motion the plate never rotates, so start settled and show every row ticked.
  const [checked, setChecked] = useState(reduced);
  // Flips true once the carousel first advances. Guards the opening dwell, when
  // the row above centre has not been through a check yet and reads "Ready".
  const [rotated, setRotated] = useState(false);

  useEffect(() => {
    if (reduced) return;
    setChecked(false);
    if (active !== 0) setRotated(true);
    const tick = window.setTimeout(() => setChecked(true), CHECK_MS);
    return () => window.clearTimeout(tick);
  }, [active, reduced]);

  return (
    <div className="absolute inset-0 overflow-hidden">
      {VERIFY_SERVICES.map((service, i) => {
        // Signed distance from the centre, wrapped so the carousel loops.
        let rel = i - active;
        if (rel > count / 2) rel -= count;
        if (rel < -count / 2) rel += count;
        const dist = Math.abs(rel);
        const isActive = rel === 0;
        // Verified in the centre once its check lands, and for the one count it
        // sits directly above. Anywhere else — including once it wraps to the
        // bottom — it resets to "Ready".
        const isVerified = reduced
          ? true
          : isActive
            ? checked
            : rel === -1 && rotated;
        const Icon = service.icon;

        return (
          <div
            key={service.label}
            data-testid="home-core-verify-panel"
            data-active={isActive}
            data-verified={isVerified}
            className={cn(
              "absolute inset-x-5 top-1/2 flex items-center gap-3 rounded-xl border bg-card px-3.5 py-3 transition-all duration-700 ease-out",
              isActive && "shadow-lg",
            )}
            style={{
              transform: `translateY(calc(-50% + ${rel * 62}px)) translateX(${-dist * 8}px) scale(${isActive ? 1.05 : 1 - dist * 0.06})`,
              opacity: dist >= 2 ? 0 : isActive ? 1 : 0.5,
              zIndex: count - dist,
            }}
          >
            <span
              className={cn(
                "grid size-8 shrink-0 place-items-center rounded-lg transition-colors",
                isVerified
                  ? "bg-success/15 text-success"
                  : "bg-primary/15 text-primary",
              )}
            >
              <Icon className="size-4" />
            </span>
            <span className="min-w-0 flex-1 truncate text-sm font-medium">
              {service.name}
            </span>
            <StatusPill state={isVerified ? "ok" : isActive ? "check" : "idle"} />
          </div>
        );
      })}
    </div>
  );
};

export const VERIFY_SHOWCASE: CoreShowcase = {
  labels: VERIFY_SERVICES.map((service) => service.label),
  render: (active) => <VerifyCarousel active={active} />,
};
